import {BulkSourceUpdate, FileId, Source, SourceId, SourceUsageType} from "platformed-client/types";
import {invalidateQueries, q} from "../../state"; // Updated import to include q
import {platformedClient} from "..";

export async function get(sourceId: SourceId): Promise<Source> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/sources/{source_id}", {
    path: {source_id: sourceId},
  });
}

export async function delete_(sourceId: SourceId): Promise<string> {
  const res = await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/sources/{source_id}", {
    path: {source_id: sourceId},
  });
  await invalidateQueries([
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    // Deleting a source can clear an error badge on its scope.
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export async function reprocess(sourceId: SourceId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/sources/{source_id}/reprocess",
    {path: {source_id: sourceId}},
  );
  await invalidateQueries([
    q.tprm.source.filter(sourceId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    // Reprocessing clears the error and will (eventually) update the badge.
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export async function update(sourceId: SourceId, sourceUsageType: SourceUsageType, update: boolean): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/sources/{source_id}/usage/{usage}",
    {path: {source_id: sourceId, usage: sourceUsageType}, body: update},
  );
  await invalidateQueries([
    q.tprm.source.filter(sourceId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
  ]);
  return res;
}

export async function updateDocumentFile(sourceId: SourceId, fileId: FileId): Promise<string> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/sources/{source_id}/file", {
    path: {source_id: sourceId},
    body: fileId,
  });
  await invalidateQueries([
    q.tprm.source.filter(sourceId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    // Replacing the file will trigger reprocessing, resetting any prior error.
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export async function updateNonAuthoritativeMarkdown(sourceId: SourceId, markdown: string) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/sources/{source_id}/non_authoritative_markdown",
    {path: {source_id: sourceId}, body: markdown},
  );
  await invalidateQueries([
    q.tprm.source.filter(sourceId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    // Markdown update triggers reprocessing, potentially clearing an error.
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export async function updateNotes(sourceId: SourceId, notes: string) {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/sources/{source_id}/notes", {
    path: {source_id: sourceId},
    body: notes,
  });
  await invalidateQueries([
    q.tprm.source.filter(sourceId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export async function bulkUpdate(update: BulkSourceUpdate) {
  const res = await platformedClient.internalRequest("patch", "/region/{region}/api/tprm/sources", {body: update});
  await invalidateQueries([
    q.tprm.source.filter(),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    // Bulk delete can clear error badges across multiple scopes.
    q.tprm.engagementScopeBadges.filter(),
  ]);
  return res;
}

export const downloadMany = async (selectedIds: SourceId[]) => {
  const a = document.createElement("a");
  a.href = platformedClient.internalUrl("get", "/region/{region}/api/tprm/sources/download", {
    query: {source_id: selectedIds},
  });
  a.target = "_blank";
  a.click();
};
